import { clearSession, saveSession, state, syncSessionFromStorage } from './state';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message);
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  auth?: boolean;
  signal?: AbortSignal;
};

const REFRESH_AHEAD_MS = 2 * 60 * 1000;
let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(staleToken: string | null): Promise<boolean> {
  // Another tab may have refreshed while we waited for the lock.
  syncSessionFromStorage();
  if (state.token && state.token !== staleToken) return true;

  const refreshToken = state.refreshToken;
  if (!refreshToken) return false;
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (res.status === 401 || res.status === 403) {
      clearSession();
      return false;
    }
    if (!res.ok) return false; // network/5xx/429 — keep the session, try later
    const data = (await res.json()) as { session: { token: string; refresh_token: string; expires_at: number | null } };
    saveSession(data.session);
    return true;
  } catch {
    return false;
  }
}

/** Serialized across tabs via Web Locks so refresh_token is rotated only once. */
export function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const staleToken = state.token;
  const run = (): Promise<boolean> => doRefresh(staleToken);
  const promise: Promise<boolean> = (
    navigator.locks ? (navigator.locks.request('gaz-auth-refresh', run) as unknown as Promise<boolean>) : run()
  ).finally(() => {
    refreshInFlight = null;
  });
  refreshInFlight = promise;
  return promise;
}

async function ensureFreshToken(): Promise<void> {
  if (state.token && state.expiresAt && state.expiresAt - Date.now() < REFRESH_AHEAD_MS) await refreshSession();
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
    if (opts.auth && state.token) headers.Authorization = `Bearer ${state.token}`;
    const init: RequestInit = { method: opts.method ?? 'GET', headers };
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
    if (opts.signal) init.signal = opts.signal;
    return fetch(path, init);
  };

  if (opts.auth) await ensureFreshToken();
  let res = await send();
  if (res.status === 401 && opts.auth && state.refreshToken) {
    if (await refreshSession()) res = await send();
  }
  return parse<T>(res);
}

async function parse<T>(res: Response): Promise<T> {
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; code?: string };
    throw new ApiRequestError(err.error ?? `HTTP ${res.status}`, res.status, err.code);
  }
  return data as T;
}

export async function apiUpload(file: Blob): Promise<string> {
  await ensureFreshToken();
  const send = (): Promise<Response> =>
    fetch('/api/uploads', {
      method: 'POST',
      headers: { 'Content-Type': file.type, Authorization: `Bearer ${state.token ?? ''}` },
      body: file,
    });
  let res = await send();
  if (res.status === 401 && (await refreshSession())) res = await send();
  const data = await parse<{ url: string }>(res);
  return data.url;
}

// Refresh proactively when the tab regains focus.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') void ensureFreshToken();
});
